import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  InlineStack,
  Text,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
  TextField,
  Banner,
} from '@shopify/polaris';
import { returnsApi } from '@/services';
import type { QcQueueItem } from '@/types';

export default function ReturnsQc() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedAsset, setSelectedAsset] = useState<QcQueueItem | null>(null);
  const [showQcModal, setShowQcModal] = useState(false);
  const [qcNotes, setQcNotes] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['qc-queue', page],
    queryFn: () => returnsApi.getQcQueue({ page, limit }),
  });

  const qcMutation = useMutation({
    mutationFn: (data: { serialNumber: string; passed: boolean; notes?: string }) =>
      returnsApi.processQc(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc-queue'] });
      queryClient.invalidateQueries({ queryKey: ['returns-stats'] });
      setShowQcModal(false);
      setSelectedAsset(null);
      setQcNotes('');
    },
  });

  const handleOpenQc = useCallback((asset: QcQueueItem) => {
    setSelectedAsset(asset);
    setQcNotes('');
    setShowQcModal(true);
  }, []);

  const handleQcPass = useCallback(() => {
    if (!selectedAsset) return;
    qcMutation.mutate({
      serialNumber: selectedAsset.serialNumber,
      passed: true,
      notes: qcNotes || undefined,
    });
  }, [selectedAsset, qcNotes, qcMutation]);

  const handleQcFail = useCallback(() => {
    if (!selectedAsset || !qcNotes.trim()) return;
    qcMutation.mutate({
      serialNumber: selectedAsset.serialNumber,
      passed: false,
      notes: qcNotes,
    });
  }, [selectedAsset, qcNotes, qcMutation]);

  const rows = (data?.data || []).map((asset) => [
    asset.serialNumber,
    asset.productSku,
    asset.productName,
    asset.rentalInfo?.orderNumber || 'N/A',
    asset.rentalInfo?.clinicName || 'N/A',
    <Badge key={asset.id}>QC Hold</Badge>,
    <Button key={`action-${asset.id}`} onClick={() => handleOpenQc(asset)}>
      QC Inspect
    </Button>,
  ]);

  if (error) {
    return (
      <Page title="QC Queue">
        <Card>
          <EmptyState
            heading="Error loading QC queue"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="QC Queue">
      <Card padding="0">
        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No items pending QC"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>All returned items have been inspected.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Serial', 'SKU', 'Product', 'Order', 'Clinic', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            {data.meta.total} items pending QC
          </Text>
        </Box>
      )}

      {/* QC Modal */}
      <Modal
        open={showQcModal}
        onClose={() => {
          setShowQcModal(false);
          setSelectedAsset(null);
          setQcNotes('');
        }}
        title={`QC Inspection - ${selectedAsset?.serialNumber}`}
      >
        <Modal.Section>
          {selectedAsset && (
            <BlockStack gap="400">
              <Card>
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Product</Text>
                    <Text as="span">{selectedAsset.productName}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">SKU</Text>
                    <Text as="span">{selectedAsset.productSku}</Text>
                  </InlineStack>
                  {selectedAsset.rentalInfo && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">From Order</Text>
                      <Text as="span">{selectedAsset.rentalInfo.orderNumber}</Text>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>

              <TextField
                label="Inspection Notes"
                value={qcNotes}
                onChange={setQcNotes}
                multiline={3}
                placeholder="Enter any observations or issues..."
                helpText="Notes are required if QC fails"
                autoComplete="off"
              />

              <InlineStack gap="200">
                <div style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    tone="success"
                    onClick={handleQcPass}
                    loading={qcMutation.isPending}
                  >
                    Pass - Mark Ready
                  </Button>
                </div>
                <div style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    tone="critical"
                    onClick={handleQcFail}
                    loading={qcMutation.isPending}
                    disabled={!qcNotes.trim()}
                  >
                    Fail - Send to Repair
                  </Button>
                </div>
              </InlineStack>

              {!qcNotes.trim() && (
                <Banner tone="info">
                  Add notes to describe any issues before marking as failed.
                </Banner>
              )}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
